import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "./useAuth";

export interface AITipRow {
  id: string;
  business_id: string;
  condition_id: string;
  tip_text: string;
  tip_data: Record<string, unknown> | null;
  priority: number;
  shown_at: string | null;
  dismissed_at: string | null;
  expires_at: string;
  created_at: string;
}

export function useAITip() {
  const { businessId } = useAuth();

  const {
    data: tip,
    refetch,
    isLoading,
  } = useQuery({
    queryKey: ["ai_tip", businessId],
    queryFn: async (): Promise<AITipRow | null> => {
      if (!businessId) return null;
      const { data, error } = await supabase
        .from("ai_tips")
        .select("*")
        .eq("business_id", businessId)
        .is("dismissed_at", null)
        .gt("expires_at", new Date().toISOString())
        .order("priority", { ascending: true })
        .limit(1)
        .maybeSingle();
      if (error) throw error;
      return data as AITipRow | null;
    },
    staleTime: 1000 * 60 * 30,
    enabled: !!businessId,
  });

  const markShown = async (tipId: string) => {
    const row = tip;
    if (!row || row.id !== tipId || row.shown_at) return;
    await supabase
      .from("ai_tips")
      .update({ shown_at: new Date().toISOString() })
      .eq("id", tipId);
  };

  const dismiss = async (tipId: string) => {
    await supabase
      .from("ai_tips")
      .update({ dismissed_at: new Date().toISOString() })
      .eq("id", tipId);
    await refetch();
  };

  return { tip, markShown, dismiss, isLoading };
}
